import cn from 'classnames'
import { Nullable } from 'legacy/lut/classes/models/utils'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import {
  FormNumberInput,
  resetModifiedDataByField,
  useGetFormDataField,
} from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'

import { LOCATIONS_SEPARATOR, PARTNER_BASIC_INFO_FORM_KEY } from '../../configs'
import { TRANSLATION_KEYS } from '../../translations'
import { LocationsEditorDialog } from '../locationsEditorDialog/LocationsEditorDialog'

import styles from './LocationsAndCount.module.scss'

interface ILocationsAndCountProps {
  isApplicable: boolean
  count: Nullable<number>
  countFieldName: string
  locations: string
  locationsFieldName: string
  isCountRequiredForCompleteness?: boolean
  isLocationsRequiredForCompleteness?: boolean
  isCountEnabled?: boolean
  isLocationsEnabled?: boolean
}

export const LocationsAndCount: FC<ILocationsAndCountProps> = ({
  isApplicable,
  countFieldName,
  count: defaultCount,
  locations: initialLocations,
  locationsFieldName,
  isCountEnabled = true,
  isLocationsEnabled = true,
}) => {
  /** -------------------- State ----------------------- */
  const [showLocationEditor, setShowLocationEditor] = useState(false)

  /** -------------------- Hooks ----------------------- */
  const dispatch = useAppDispatch()
  const [countValue] = useGetFormDataField<Nullable<number>>(
    PARTNER_BASIC_INFO_FORM_KEY,
    countFieldName
  )
  const locationsFormField = useGetFormDataField<string>(
    PARTNER_BASIC_INFO_FORM_KEY,
    locationsFieldName
  )
  /** -------------------- Variables ----------------------- */
  const locationsString = locationsFormField[0]
  const isLocationsModified = locationsFormField[2]

  const locationValue = useMemo(() => {
    return isLocationsModified ? locationsString : initialLocations
  }, [isLocationsModified, locationsString, initialLocations])

  const locations = useMemo(() => {
    return locationValue.split(LOCATIONS_SEPARATOR)
  }, [locationValue])

  const showCountCompletenessWarning = useMemo(() => {
    if (!isApplicable) {
      return false
    }

    return countValue === null || countValue === 0
  }, [isApplicable, countValue])

  const showLocationsCompletenessWarning = useMemo(() => {
    return isApplicable && !locationValue
  }, [isApplicable, locationValue])

  /** -------------------- Methods ----------------------- */
  const openLocationEditor = useCallback(() => {
    if (isLocationsEnabled && isApplicable) {
      setShowLocationEditor(true)
    }
  }, [isLocationsEnabled, isApplicable])

  const closeLocationEditor = useCallback(() => {
    setShowLocationEditor(false)
  }, [])

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    return () => {
      dispatch(
        resetModifiedDataByField({
          formKey: PARTNER_BASIC_INFO_FORM_KEY,
          field: locationsFieldName,
        })
      )

      dispatch(
        resetModifiedDataByField({
          formKey: PARTNER_BASIC_INFO_FORM_KEY,
          field: countFieldName,
        })
      )
    }
  }, [dispatch, locationsFieldName, countFieldName])

  return (
    <div className={styles.root}>
      {isCountEnabled && (
        <div className={styles.countWrapper}>
          <div className={styles.countValue}>
            <Tooltip
              disabled={!showCountCompletenessWarning}
              tipContent={() => (
                <Translation text={TRANSLATION_KEYS.affectionOnCompleteness} span />
              )}
            >
              <FormNumberInput
                isDisabled={!isApplicable}
                defaultValue={defaultCount}
                field={countFieldName}
                formKey={PARTNER_BASIC_INFO_FORM_KEY}
                label={TRANSLATION_KEYS.locationsAndCount.countLabel}
                className={cn({
                  [styles.warning]: showCountCompletenessWarning,
                })}
              />
            </Tooltip>
          </div>
        </div>
      )}

      {isLocationsEnabled && (
        <Tooltip
          disabled={!showLocationsCompletenessWarning}
          className={styles.tooltipWrapper}
          tipContent={() => (
            <Translation text={TRANSLATION_KEYS.valueRequiredForCompleteness} span />
          )}
        >
          <div
            className={cn(styles.locationWrapper, {
              [styles.clickable]: isLocationsEnabled && isApplicable,
              [styles.warning]: showLocationsCompletenessWarning,
              [styles.disabledLocations]: !isApplicable,
            })}
            onClick={openLocationEditor}
          >
            <div className={styles.iconContainer}>
              <i className="kbase kbase-omni-location-pin" />
            </div>
            <div className={styles.locationContent}>
              <div className={styles.locationValue}>{locations[0] || '-'}</div>
              {isApplicable && (
                <div className={styles.locationLabel}>
                  <Translation
                    text={TRANSLATION_KEYS.locationsAndCount.locationsClickToEditLabel}
                  />
                </div>
              )}
            </div>
          </div>
        </Tooltip>
      )}

      {showLocationEditor && (
        <LocationsEditorDialog
          initialLocations={locationValue}
          fieldName={locationsFieldName}
          onCancel={closeLocationEditor}
        />
      )}
    </div>
  )
}
